"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion } from "framer-motion";
import { Loader2, X, type LucideIcon } from "lucide-react";
import {
  TITLE_CLASS,
  SUB_CLASS,
  RADIUS,
  SPRING_IN,
  tone,
  type AlertTone,
} from "@/lib/alert-theme";

/**
 * IcyDialogShell — the shared modal chrome used by every alert-management
 * edit dialog. Matches the ICY tone tokens exactly so the modal *feels* like
 * a natural extension of the header card that triggered it.
 *
 * Why a custom shell instead of the shadcn <Dialog>:
 *   - We want the backdrop darker + icy-tinted (not the default black/80).
 *   - We want the exact surface/ring colors from alert-theme.ts so it stays
 *     in-brand with AlertCardShell in display.tsx.
 *   - We still use Radix primitives under the hood for accessibility.
 */
const getFocusRing = (viewMode?: string) =>
  `focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background ${viewMode === "signal" ? "focus-visible:ring-emerald-300/60" : "focus-visible:ring-sky-300/60"}`;

interface IcyDialogShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle?: string;
  Icon: LucideIcon;
  children: React.ReactNode;
  viewMode?: string;
  /**
   * Footer actions — rendered at the bottom, right-aligned.
   * Typically `<SecondaryDialogButton>` (Cancel) + `<PrimaryDialogButton>` (Save).
   */
  footer?: React.ReactNode;
  /** Wider modal for schedule picker; default = 480px. */
  size?: "sm" | "md";
  /** Icon + border accent — icy default; amber/emerald for pause/resume confirms. */
  tone?: AlertTone;
  /** Tighter padding for small confirmation dialogs. */
  compact?: boolean;
  contentTestId?: string;
  closeButtonTestId?: string;
}

export function IcyDialogShell({
  open,
  onOpenChange,
  title,
  subtitle,
  Icon,
  children,
  footer,
  size = "sm",
  tone: toneKey = "icy",
  compact = false,
  contentTestId,
  closeButtonTestId,
  viewMode,
}: IcyDialogShellProps) {
  const t = tone(toneKey);
  const width = size === "md" ? "max-w-[560px]" : compact ? "max-w-[400px]" : "max-w-[480px]";
  const padX = compact ? "px-4" : "px-5";
  const padHeaderY = compact ? "py-3" : "py-4";
  const padBodyY = compact ? "py-3" : "py-4";
  const padFooterY = compact ? "py-2.5" : "py-3";

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-slate-950/75 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          className={`fixed left-1/2 top-1/2 z-50 w-[92vw] ${width} max-h-[92dvh] -translate-x-1/2 -translate-y-1/2 focus:outline-none`}
          onInteractOutside={(event) => {
            // Keep the dialog open when the outside-click landed on the
            // buddy tour's bubble. The buddy tour highlights elements
            // INSIDE this dialog (preset grid, plain-English input,
            // etc.) and renders its own bubble in a separate portal —
            // Radix's dismissable layer treats clicks on the bubble as
            // "outside the dialog" and would auto-close us, leaving the
            // tour stranded with no anchor.
            // The bubble carries `data-buddy-bubble="true"`; if the
            // pointer/focus event originated there we cancel the close.
            const original = (event.detail.originalEvent ?? event) as Event;
            const target = original.target as Element | null;
            if (target?.closest("[data-buddy-bubble]")) {
              event.preventDefault();
            }
          }}
        >
          <motion.div
            data-testid={contentTestId}
            {...SPRING_IN}
            className={`${RADIUS.card} border ${t.ring} ${t.surface} backdrop-blur-xl shadow-[0_20px_60px_-12px_rgba(2,6,23,0.9)] overflow-hidden max-h-[92dvh] flex flex-col`}
          >
            {/* Header ----------------------------------------------------- */}
            <div className={`flex items-center gap-3 ${padX} ${padHeaderY} border-b border-slate-300/10`}>
              <div
                className={`w-8 h-8 ${RADIUS.chip} ${t.iconBg} flex items-center justify-center shrink-0 border ${t.ring}`}
              >
                <Icon className={`w-3.5 h-3.5 ${t.iconFg}`} strokeWidth={2.2} />
              </div>
              <div className="flex flex-col min-w-0 flex-1 gap-0.5">
                <DialogPrimitive.Title className={TITLE_CLASS}>
                  {title}
                </DialogPrimitive.Title>
                {subtitle && <span className={SUB_CLASS}>{subtitle}</span>}
              </div>
              <DialogPrimitive.Close
                data-testid={closeButtonTestId}
                className={`w-7 h-7 rounded-md flex items-center justify-center text-slate-300 hover:text-slate-50 hover:bg-slate-100/5 transition-colors ${getFocusRing(viewMode)}`}
              >
                <X className="w-4 h-4" strokeWidth={2} />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            </div>

            {/* Body ------------------------------------------------------- */}
            <div className={`${padX} ${padBodyY} flex flex-col gap-3 overflow-y-auto`}>{children}</div>

            {/* Footer ----------------------------------------------------- */}
            {footer && (
              <div className={`flex items-center justify-end gap-2 ${padX} ${padFooterY} border-t border-slate-300/10 bg-slate-950/35`}>
                {footer}
              </div>
            )}
          </motion.div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Buttons — same visual language as PrimaryButton/SecondaryButton in
// components/tool-displays/alerts/display.tsx, re-exported here so modals can
// use them without importing from a tool-display file.
// ─────────────────────────────────────────────────────────────────────────────

export function PrimaryDialogButton({
  onClick,
  disabled,
  busy,
  Icon,
  children,
  variant = "icy",
  size = "default",
  type = "button",
  viewMode,
}: {
  viewMode?: string;
  onClick?: () => void;
  disabled?: boolean;
  busy?: boolean;
  Icon?: LucideIcon;
  children: React.ReactNode;
  variant?: "icy" | "pearl" | "amber" | "emerald";
  size?: "default" | "sm";
  type?: "button" | "submit";
}) {
  const sizeClass =
    size === "sm" ? "h-7 px-3 text-xs" : "h-9 px-4 text-sm";
  const base =
    variant === "icy"
      ? "bg-sky-200 hover:bg-sky-100 text-slate-950"
      : variant === "amber"
        ? "bg-amber-300 hover:bg-amber-200 text-slate-950"
        : variant === "emerald"
          ? "bg-emerald-300 hover:bg-emerald-200 text-slate-950"
          : "bg-slate-100 hover:bg-white text-slate-950";
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-1.5 ${sizeClass} ${RADIUS.button} ${base} disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors ${getFocusRing(viewMode)}`}
    >
      {busy ? (
        <Loader2 className={`${size === "sm" ? "w-3 h-3" : "w-4 h-4"} animate-spin`} />
      ) : Icon ? (
        <Icon className={`${size === "sm" ? "w-3 h-3" : "w-4 h-4"}`} strokeWidth={2.4} />
      ) : null}
      {children}
    </button>
  );
}

export function SecondaryDialogButton({
  onClick,
  disabled,
  Icon,
  children,
  size = "default",
  viewMode,
}: {
  viewMode?: string;
  onClick?: () => void;
  disabled?: boolean;
  Icon?: LucideIcon;
  children: React.ReactNode;
  size?: "default" | "sm";
}) {
  const sizeClass =
    size === "sm" ? "h-7 px-3 text-xs" : "h-9 px-4 text-sm";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-1.5 ${sizeClass} ${RADIUS.button} border border-slate-400/30 bg-transparent hover:bg-slate-100/5 hover:border-slate-300/50 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-slate-100 transition-colors ${getFocusRing(viewMode)}`}
    >
      {Icon ? (
        <Icon
          className={`${size === "sm" ? "w-3 h-3" : "w-4 h-4"}`}
          strokeWidth={2.4}
        />
      ) : null}
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Inputs — matched to the ICY card surface so inline fields look native.
// ─────────────────────────────────────────────────────────────────────────────

export const getDialogInputClass = (viewMode?: string) =>
  `w-full h-10 px-3 rounded-md bg-slate-950/50 border border-slate-300/20 text-sm text-slate-50 placeholder:text-slate-400/70 focus:outline-none transition-colors ${viewMode === "signal" ? "focus:border-emerald-300/50 focus:ring-1 focus:ring-emerald-300/30" : "focus:border-sky-300/50 focus:ring-1 focus:ring-sky-300/30"}`;

export const getDialogTextareaClass = (viewMode?: string) =>
  `w-full min-h-[90px] px-3 py-2 rounded-md bg-slate-950/50 border border-slate-300/20 text-sm text-slate-50 placeholder:text-slate-400/70 focus:outline-none transition-colors resize-none leading-relaxed ${viewMode === "signal" ? "focus:border-emerald-300/50 focus:ring-1 focus:ring-emerald-300/30" : "focus:border-sky-300/50 focus:ring-1 focus:ring-sky-300/30"}`;

export const DIALOG_LABEL_CLASS =
  "text-[11px] uppercase tracking-wider font-semibold text-slate-200/80 mb-1.5 inline-block";

export const DIALOG_ERROR_CLASS =
  "text-xs text-rose-300 mt-1.5 flex items-center gap-1";
